"use client";

import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { useEffect, useRef, useCallback } from "react";
import { useServerAccessToken } from "@/lib/contexts/AuthContext";
import { PageTypeGlobal } from "@/lib/types";
import { redirectToLoginAfterAuthFailure } from "@/lib/utils/auth/redirectToLogin";

/**
 * Checks if a route is public (doesn't require authentication)
 */
function isPublicRoute(pathname: string): boolean {
  // Public global routes
  if (
    pathname === PageTypeGlobal.Home ||
    pathname.startsWith(PageTypeGlobal.Classes) ||
    pathname.startsWith(PageTypeGlobal.Teachers) ||
    pathname.startsWith(PageTypeGlobal.Locations) ||
    pathname.startsWith(PageTypeGlobal.Contact) ||
    pathname.startsWith(PageTypeGlobal.Terms) ||
    pathname.startsWith("/contractor-packet")
  ) {
    return true;
  }

  // Auth routes (public)
  if (
    pathname.startsWith("/portal/auth/") ||
    pathname.startsWith("/admin/auth/")
  ) {
    return true;
  }

  // Error pages
  if (
    pathname === "/forbidden" ||
    pathname === "/403" ||
    pathname === "/404"
  ) {
    return true;
  }

  return false;
}

/**
 * Hook to get access token and handle token expiration
 * 
 * This hook uses a passive approach - it relies on NextAuth's built-in
 * refresh mechanism (via JWT callback and refetchInterval) instead of
 * manually calling update(). This prevents infinite loops and server overload.
 * 
 * It only handles redirects when:
 * 1. Session has a refresh error
 * 2. Token is expired AND user is not authenticated
 * 
 * Token refresh is handled automatically by NextAuth's JWT callback.
 */
export function useAccessToken(): string | undefined {
  const { data: session, status } = useSession();
  const serverAccessToken = useServerAccessToken();
  const pathname = usePathname();
  const clientAccessToken = (session as any)?.accessToken as string | undefined;
  const accessToken = clientAccessToken ?? serverAccessToken;
  const expiresAt = (session as any)?.expiresAt as number | undefined;
  const sessionError = (session as any)?.error;

  // Track if we're already redirecting to prevent multiple redirects
  const isRedirectingRef = useRef(false);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Track last processed error to prevent reprocessing same error
  const lastProcessedErrorRef = useRef<string | undefined>(undefined);
  // Track last redirect pathname to prevent redirecting to same page multiple times
  const lastRedirectPathnameRef = useRef<string | undefined>(undefined);
  // Debounce timer for redirects
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // Memoized redirect function with debounce
  const handleRedirect = useCallback((currentPathname: string) => {
    if (!isMountedRef.current) {
      return;
    }

    // Prevent redirecting to the same page multiple times
    if (
      lastRedirectPathnameRef.current === currentPathname &&
      isRedirectingRef.current
    ) {
      return;
    }

    // Clear any pending redirect
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }

    // Debounce redirect to prevent rapid-fire redirects
    redirectTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current || isRedirectingRef.current) {
        return;
      }

      isRedirectingRef.current = true;
      lastRedirectPathnameRef.current = currentPathname;
      void redirectToLoginAfterAuthFailure(currentPathname);
    }, 100); // 100ms debounce
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !isMountedRef.current) {
      return;
    }

    // Don't redirect if it's a public route
    if (isPublicRoute(pathname)) {
      // Reset redirect tracking when on public route
      if (isRedirectingRef.current) {
        isRedirectingRef.current = false;
        lastRedirectPathnameRef.current = undefined;
      }
      return;
    }

    // Wait for NextAuth to finish hydrating session after client navigation
    // (e.g. public marketing page → portal dashboard).
    if (status === "loading") {
      return;
    }

    // Check if session has error (refresh failed)
    // NextAuth's JWT callback already tried to refresh and failed
    if (sessionError === "RefreshAccessTokenError") {
      // Only process if this is a new error (not already processed)
      if (
        lastProcessedErrorRef.current === "RefreshAccessTokenError" &&
        isRedirectingRef.current
      ) {
        return;
      }
      lastProcessedErrorRef.current = "RefreshAccessTokenError";
      handleRedirect(pathname);
      return;
    }

    // Reset error tracking if error is cleared
    if (!sessionError && lastProcessedErrorRef.current) {
      lastProcessedErrorRef.current = undefined;
    }

    // Only redirect if token is expired AND user is not authenticated
    // If user is authenticated but token is expired, NextAuth's JWT callback
    // will handle the refresh automatically on the next session check
    if (expiresAt && Date.now() >= expiresAt) {
      // Session resolved but user is not signed in - redirect to login.
      if (status === "unauthenticated") {
        if (!isRedirectingRef.current) {
          handleRedirect(pathname);
        }
        return;
      }
      // If authenticated but token expired, NextAuth will refresh it automatically
      // We don't need to do anything here - just wait for NextAuth's refetchInterval
      // or the next useSession() call to trigger the JWT callback refresh
      return;
    }

    // Reset redirect tracking if token is valid
    if (expiresAt && Date.now() < expiresAt && isRedirectingRef.current) {
      isRedirectingRef.current = false;
      lastRedirectPathnameRef.current = undefined;
    }
  }, [expiresAt, pathname, sessionError, status, handleRedirect]);

  return accessToken;
}
